<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';
import { useAlert } from 'dashboard/composables';

import ConversationBox from 'dashboard/components/widgets/conversation/ConversationBox.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import SalesInboxPickerDialog from '../components/SalesInboxPickerDialog.vue';
import ChatList from 'dashboard/components/ChatList.vue';

import {
  searchContacts,
  createNewContact,
  fetchContactableInboxes,
  prepareNewMessagePayload,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import types from 'dashboard/store/mutation-types';
import { useUISettings } from 'dashboard/composables/useUISettings';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { uiSettings } = useUISettings();

const currentUser = useMapGetter('getCurrentUser');
const chatList = useMapGetter('getAllConversations');
const currentChat = useMapGetter('getSelectedChat');
const hasAllPageEnded = useFunctionGetter(
  'conversationPage/getHasEndReached',
  'all'
);

const inboxPickerRef = ref(null);
const isCreating = ref(false);

const shouldShowSidebar = computed(() => {
  if (!currentChat.value?.id) return false;
  const { is_contact_sidebar_open: isContactSidebarOpen } = uiSettings.value;
  return isContactSidebarOpen;
});

const pinSalesAssistPaginationEnd = () => {
  if (hasAllPageEnded.value) return;
  store.dispatch('conversationPage/setEndReached', { filter: 'all' });
};

watch(hasAllPageEnded, isEnded => {
  if (!isEnded) {
    pinSalesAssistPaginationEnd();
  }
});

onMounted(() => {
  pinSalesAssistPaginationEnd();
});

const setActiveChat = conversationId => {
  const chat = chatList.value.find(c => c.id === conversationId);
  if (chat && chat.id !== currentChat.value?.id) {
    store.dispatch('setActiveChat', { data: chat });
  }
};

const fetchConversationIfUnavailable = async convId => {
  const conversationId = Number.parseInt(convId, 10);
  const chat = chatList.value.find(c => c.id === conversationId);
  if (!chat) {
    await store.dispatch('getConversation', conversationId);
  }
  setActiveChat(conversationId);
};

const findMatchingInbox = (contactableInboxes, selectedInboxId) => {
  return contactableInboxes?.find(
    inbox => Number(inbox.id) === Number(selectedInboxId)
  );
};

// Watch conversationId to set active chat
watch(
  () => props.conversationId,
  newId => {
    if (newId) {
      fetchConversationIfUnavailable(newId);
    } else {
      store.dispatch('clearSelectedState');
    }
  },
  { immediate: true }
);

const findOrCreateAgentContact = async email => {
  const contacts = await searchContacts({ keys: ['email'], query: email });
  const matchedContact = contacts.find(
    contact => contact.email?.toLowerCase() === email.toLowerCase()
  );
  if (matchedContact) return matchedContact;
  return createNewContact(email);
};

const handleInboxSelected = async selectedInbox => {
  if (isCreating.value) return;

  const agentEmail = currentUser.value?.email;
  if (!agentEmail) {
    useAlert(t('CHAT_LIST.NEW_BOT_CHAT.MISSING_EMAIL'));
    return;
  }

  isCreating.value = true;
  try {
    inboxPickerRef.value?.close();
  } catch (error) {
    if (error?.name !== 'InvalidStateError') {
      throw error;
    }
  }

  try {
    const contact = await findOrCreateAgentContact(agentEmail);
    if (!contact?.id) {
      useAlert(t('CHAT_LIST.NEW_BOT_CHAT.CONTACT_ERROR'));
      return;
    }

    const contactableInboxes = await fetchContactableInboxes(contact.id);
    const matchingInbox = findMatchingInbox(
      contactableInboxes,
      selectedInbox.id
    );

    if (!matchingInbox?.sourceId) {
      useAlert(t('CHAT_LIST.NEW_BOT_CHAT.NO_API_INBOX'));
      return;
    }

    const targetInbox = {
      ...selectedInbox,
      sourceId: matchingInbox.sourceId,
    };

    const payload = prepareNewMessagePayload({
      targetInbox,
      selectedContact: contact,
      message: t('CHAT_LIST.NEW_BOT_CHAT.INITIAL_MESSAGE'),
      currentUser: currentUser.value,
    });
    payload.message.message_type = 'incoming';
    payload.status = 'pending';
    if (!payload.sourceId) {
      payload.sourceId = contact.email || `agent-${currentUser.value?.id}`;
    }

    const data = await store.dispatch('contactConversations/create', {
      params: payload,
      isFromWhatsApp: false,
    });

    store.commit(types.UPDATE_CONVERSATION, {
      ...data,
      dataFetched: true,
      allMessagesLoaded: true,
      messages: data.messages || [],
    });

    // Navigate within Sales Assistant
    await router.push({
      name: 'sales_assist_conversation',
      params: {
        accountId: data.account_id,
        conversation_id: data.id,
      },
    });
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      t('CHAT_LIST.SALES_ASSIST.ERROR');
    useAlert(errorMessage);
  } finally {
    isCreating.value = false;
  }
};

const handleNewChat = () => {
  const inboxes = inboxPickerRef.value?.salesInboxes || [];
  if (inboxes.length === 1) {
    useAlert(
      t('CHAT_LIST.SALES_ASSIST.DEFAULT_INBOX_SELECTED', {
        inboxName: inboxes[0].name,
      }),
      { duration: 5000 }
    );
    handleInboxSelected(inboxes[0]);
  } else {
    inboxPickerRef.value?.open();
  }
};
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <!-- Left panel: conversation list -->
    <ChatList is-sales-assist-mode @new-chat="handleNewChat" />

    <!-- Right panel: conversation view -->
    <ConversationBox v-if="conversationId" :is-on-expanded-layout="false" />
    <div
      v-else
      class="flex flex-col items-center justify-center flex-1 bg-n-background"
    >
      <p class="text-base text-n-slate-11">
        {{ t('CHAT_LIST.SALES_ASSIST.EMPTY_TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ t('CHAT_LIST.SALES_ASSIST.EMPTY_SUBTITLE') }}
      </p>
    </div>

    <ConversationSidebar v-if="shouldShowSidebar" :current-chat="currentChat" />

    <SalesInboxPickerDialog
      ref="inboxPickerRef"
      @select="handleInboxSelected"
    />
  </section>
</template>
